import { type GraphQLError } from "graphql"
// TODO: consider https://www.npmjs.com/package/@zxcvbn-ts/core instead
import { PASSWORD_MIN_SCORE, PASSWORD_SCORE_SHIFT } from "@/constants/password"
import { type Translator } from "@/features/translations"

export async function getPasswordStrength(value: string) {
    if (!value) {
        return 0
    }

    const zxcvbn = await import("zxcvbn-typescript")
    const { score } = zxcvbn.default(value)

    return score + PASSWORD_SCORE_SHIFT
}

type LegacyErrorType = {
    legacyMessage: string
    message: string
    involvedField: "currentPassword" | "newPassword"
}

function getLegacyErrors(t: Translator<"SecurityPage">) {
    return [
        {
            legacyMessage: "Your old password was entered incorrectly. Please enter it again.",
            message: t("passwordIncorrect"),
            involvedField: "currentPassword",
        },
        {
            legacyMessage: "The password is too similar to the email.",
            message: t("newPasswordTooCloseToEmail"),
            involvedField: "newPassword",
        },
        {
            legacyMessage: "New password matches current password.",
            message: t("newPasswordSameAsCurrentPassword"),
            involvedField: "newPassword",
        },
        {
            legacyMessage: "Password reused too recently.",
            message: t("newPasswordSameAsPreviousPassword"),
            involvedField: "newPassword",
        },
    ] satisfies Array<LegacyErrorType>
}

/**
 * Workaround for legacy validation errors.
 */
export function mapLegacyValidationErrorsToError(
    validationErrors: GraphQLError["extensions"]["validationErrors"],
    t: Translator<"SecurityPage">
) {
    return getLegacyErrors(t).find(({ legacyMessage }) => {
        return validationErrors?.some(({ message }) => message.includes(legacyMessage))
    })
}

export async function isPasswordStrongEnough(value: string) {
    const strength = await getPasswordStrength(value)

    return strength >= PASSWORD_MIN_SCORE
}
